import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ReclassifyByAccountInput,
  ReclassifyByAccountResult,
  BankAccount,
  BankAccountSummary,
  BankRule,
  BankTransaction,
  BankTransactionSuggestion,
  ExistingEntryMatch,
  AddManualTransactionInput,
  CreateBankRuleInput,
  CreateManualBankAccountInput,
  CreateMockBankAccountInput,
  ExchangePlaidPublicTokenInput,
  ListBankTransactionsQuery,
  MatchBankAsIntercompanyInput,
  MatchBankTransactionInput,
  MatchCandidate,
  OpenPayableItem,
  ReorderBankRulesInput,
  RuleSuggestion,
  SuggestCategoriesResult,
  UpdateBankAccountInput,
  UpdateBankRuleInput,
} from '@accounting/shared';
import { api } from '../lib/api';

// ── Bank accounts ─────────────────────────────────────────────────────────

export const bankAccountsKey = (companyId: string | null) =>
  ['bank-accounts', companyId ?? 'none'] as const;

interface BankAccountsResponse {
  bankAccounts: BankAccount[];
  plaidEnv: 'mock' | 'sandbox' | 'production';
}

export function useBankAccounts(companyId: string | null) {
  return useQuery({
    queryKey: bankAccountsKey(companyId),
    enabled: companyId !== null,
    queryFn: () =>
      api.get<BankAccountsResponse>(`/companies/${companyId}/bank-accounts`),
  });
}

// ── Plaid Link (real bank connect) ─────────────────────────────────────────

export function useCreatePlaidLinkToken(companyId: string | null) {
  return useMutation({
    mutationFn: (input?: { redirectUri?: string }) => {
      if (!companyId) throw new Error('No active company');
      return api.post<{ link_token: string; expiration: string; env: string }>(
        `/companies/${companyId}/plaid/link-token`,
        input ?? {},
      );
    },
  });
}

/**
 * Update-mode link token: creates a link_token bound to the EXISTING Plaid
 * Item so the user can re-authenticate a broken connection
 * (ITEM_LOGIN_REQUIRED / PENDING_EXPIRATION). No new access_token is
 * issued — the existing one becomes valid again once Plaid Link completes.
 */
export function useCreatePlaidUpdateLinkToken(companyId: string | null) {
  return useMutation({
    mutationFn: (input: { bankAccountId: string; redirectUri?: string }) => {
      if (!companyId) throw new Error('No active company');
      return api.post<{ link_token: string; expiration: string; env: string }>(
        `/companies/${companyId}/bank-accounts/${input.bankAccountId}/plaid/update-link-token`,
        input.redirectUri ? { redirectUri: input.redirectUri } : {},
      );
    },
  });
}

export function useExchangePlaidToken(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ExchangePlaidPublicTokenInput) => {
      if (!companyId) throw new Error('No active company');
      return api
        .post<{ bankAccount: BankAccount }>(
          `/companies/${companyId}/plaid/exchange`,
          input,
        )
        .then((r) => r.bankAccount);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: bankAccountsKey(companyId) });
    },
  });
}

export function useCreateMockBankAccount(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateMockBankAccountInput) => {
      if (!companyId) throw new Error('No active company');
      return api
        .post<{ bankAccount: BankAccount }>(
          `/companies/${companyId}/bank-accounts/mock`,
          input,
        )
        .then((r) => r.bankAccount);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: bankAccountsKey(companyId) });
    },
  });
}

export function useCreateManualBankAccount(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateManualBankAccountInput) => {
      if (!companyId) throw new Error('No active company');
      return api
        .post<{ bankAccount: BankAccount }>(
          `/companies/${companyId}/bank-accounts/manual`,
          input,
        )
        .then((r) => r.bankAccount);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: bankAccountsKey(companyId) });
    },
  });
}

export function useAddManualTransaction(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      bankAccountId,
      input,
    }: {
      bankAccountId: string;
      input: AddManualTransactionInput;
    }) => {
      if (!companyId) throw new Error('No active company');
      return api.post<{ transaction: BankTransaction }>(
        `/companies/${companyId}/bank-accounts/${bankAccountId}/transactions`,
        input,
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: bankAccountsKey(companyId) });
      qc.invalidateQueries({ queryKey: ['bank-transactions', companyId ?? 'none'] });
    },
  });
}

export function useSyncBankAccount(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (bankAccountId: string) => {
      if (!companyId) throw new Error('No active company');
      return api.post<{ result: { added: number; modified: number; removed: number; rulesMatched: number; autoPosted: number; suppressedByCeiling: number; suggested: number } }>(
        `/companies/${companyId}/bank-accounts/${bankAccountId}/sync`,
        {},
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: bankAccountsKey(companyId) });
      qc.invalidateQueries({ queryKey: ['bank-transactions', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['accounts-balances', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['journal-entries', companyId ?? 'none'] });
    },
    // Invalidate on error too — bankAccounts.lastSyncError just got updated
    // server-side; the row's stale error message must refresh.
    onError: () => {
      qc.invalidateQueries({ queryKey: bankAccountsKey(companyId) });
    },
  });
}

/**
 * Full resync: reset the Plaid cursor and re-pull every transaction on the
 * Item. Backs the "Full resync" button. Existing rows are updated in place
 * (unique plaid_transaction_id + upsert), never duplicated. Use when the
 * normal Sync returns a 400 or the incremental cursor gets stuck.
 */
export function useResyncBankAccount(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (bankAccountId: string) => {
      if (!companyId) throw new Error('No active company');
      return api.post<{ result: { added: number; modified: number; removed: number; rulesMatched: number; autoPosted: number; suppressedByCeiling: number; suggested: number } }>(
        `/companies/${companyId}/bank-accounts/${bankAccountId}/resync`,
        {},
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: bankAccountsKey(companyId) });
      qc.invalidateQueries({ queryKey: ['bank-transactions', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['accounts-balances', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['journal-entries', companyId ?? 'none'] });
    },
    onError: () => {
      qc.invalidateQueries({ queryKey: bankAccountsKey(companyId) });
    },
  });
}

export function useSeedMockTransactions(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      bankAccountId,
      txns,
    }: {
      bankAccountId: string;
      txns: { date: string; amount: number; description: string; merchantName?: string | null }[];
    }) => {
      if (!companyId) throw new Error('No active company');
      return api.post<{ inserted: number }>(
        `/companies/${companyId}/bank-accounts/${bankAccountId}/seed-mock`,
        { txns },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: bankAccountsKey(companyId) });
      qc.invalidateQueries({ queryKey: ['bank-transactions', companyId ?? 'none'] });
    },
  });
}

export function useBankAccountSummaries(companyId: string | null) {
  return useQuery({
    queryKey: ['bank-account-summaries', companyId ?? 'none'] as const,
    enabled: companyId !== null,
    queryFn: () =>
      api
        .get<{ summaries: BankAccountSummary[] }>(
          `/companies/${companyId}/bank-accounts/summary`,
        )
        .then((r) => r.summaries),
  });
}

export function useUpdateBankAccount(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateBankAccountInput }) => {
      if (!companyId) throw new Error('No active company');
      return api
        .patch<{ bankAccount: BankAccount }>(
          `/companies/${companyId}/bank-accounts/${id}`,
          input,
        )
        .then((r) => r.bankAccount);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: bankAccountsKey(companyId) });
      qc.invalidateQueries({ queryKey: ['accounts-balances', companyId ?? 'none'] });
    },
  });
}

// ── Multi-account Plaid Items ─────────────────────────────────────────────

export interface PlaidItemAccountStatus {
  plaidAccountId: string;
  name: string;
  mask: string | null;
  type: string | null;
  subtype: string | null;
  linkedBankAccountId: string | null;
  linkedGlAccountId: string | null;
  isThisRow: boolean;
}

export const plaidItemAccountsKey = (companyId: string | null, bankAccountId: string) =>
  ['plaid-item-accounts', companyId ?? 'none', bankAccountId] as const;

export function usePlaidItemAccounts(companyId: string | null, bankAccountId: string | null) {
  return useQuery({
    queryKey: plaidItemAccountsKey(companyId, bankAccountId ?? ''),
    enabled: companyId !== null && !!bankAccountId,
    queryFn: () =>
      api
        .get<{ accounts: PlaidItemAccountStatus[] }>(
          `/companies/${companyId}/bank-accounts/${bankAccountId}/plaid-item-accounts`,
        )
        .then((r) => r.accounts),
  });
}

export function useSwapPlaidAccount(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ bankAccountId, plaidAccountId }: { bankAccountId: string; plaidAccountId: string }) => {
      if (!companyId) throw new Error('No active company');
      return api
        .patch<{ bankAccount: BankAccount }>(
          `/companies/${companyId}/bank-accounts/${bankAccountId}/plaid-account`,
          { plaidAccountId },
        )
        .then((r) => r.bankAccount);
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: bankAccountsKey(companyId) });
      qc.invalidateQueries({ queryKey: plaidItemAccountsKey(companyId, vars.bankAccountId) });
    },
  });
}

export function useLinkAdditionalPlaidAccount(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      sourceBankAccountId,
      plaidAccountId,
      glAccountId,
    }: {
      sourceBankAccountId: string;
      plaidAccountId: string;
      glAccountId: string;
    }) => {
      if (!companyId) throw new Error('No active company');
      return api
        .post<{ bankAccount: BankAccount }>(
          `/companies/${companyId}/bank-accounts/${sourceBankAccountId}/link-additional`,
          { plaidAccountId, glAccountId },
        )
        .then((r) => r.bankAccount);
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: bankAccountsKey(companyId) });
      qc.invalidateQueries({ queryKey: plaidItemAccountsKey(companyId, vars.sourceBankAccountId) });
    },
  });
}

export function useDeactivateBankAccount(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (bankAccountId: string) => {
      if (!companyId) throw new Error('No active company');
      return api.delete<{ bankAccount: BankAccount }>(
        `/companies/${companyId}/bank-accounts/${bankAccountId}`,
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: bankAccountsKey(companyId) });
    },
  });
}

// ── Bank transactions ─────────────────────────────────────────────────────

export const bankTransactionsKey = (
  companyId: string | null,
  filters: Partial<ListBankTransactionsQuery>,
) =>
  [
    'bank-transactions',
    companyId ?? 'none',
    filters.bankAccountId ?? 'all',
    filters.matchStatus ?? 'any',
    filters.from ?? '',
    filters.to ?? '',
    filters.limit ?? 100,
    filters.offset ?? 0,
  ] as const;

export function useBankTransactions(
  companyId: string | null,
  filters: Partial<ListBankTransactionsQuery> = {},
) {
  return useQuery({
    queryKey: bankTransactionsKey(companyId, filters),
    enabled: companyId !== null,
    queryFn: () => {
      const params = new URLSearchParams();
      for (const [k, v] of Object.entries(filters)) {
        if (v === undefined || v === null) continue;
        params.set(k, String(v));
      }
      const qs = params.toString();
      return api.get<{ transactions: BankTransaction[]; total: number }>(
        `/companies/${companyId}/bank-transactions${qs ? '?' + qs : ''}`,
      );
    },
    // Keep the previous page visible while the next page loads so the table
    // doesn't blank out between clicks.
    placeholderData: (prev) => prev,
  });
}

export function useMatchBankTransaction(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      transactionId,
      input,
    }: {
      transactionId: string;
      input: MatchBankTransactionInput;
    }) => {
      if (!companyId) throw new Error('No active company');
      return api.post<{ transactionId: string; journalEntryId: string }>(
        `/companies/${companyId}/bank-transactions/${transactionId}/match`,
        input,
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bank-transactions', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['journal-entries', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['accounts-balances', companyId ?? 'none'] });
      // The new entry is now linked to a transaction, so it must drop out of
      // every other row's "you already posted this" suggestions.
      qc.invalidateQueries({ queryKey: existingEntryMatchesKey(companyId) });
    },
  });
}

export function useMatchCandidates(companyId: string | null, transactionId: string | null) {
  return useQuery({
    queryKey: ['match-candidates', companyId ?? 'none', transactionId ?? 'none'] as const,
    enabled: companyId !== null && !!transactionId,
    queryFn: () =>
      api
        .get<{ candidates: MatchCandidate[] }>(
          `/companies/${companyId}/bank-transactions/${transactionId}/match-candidates`,
        )
        .then((r) => r.candidates),
  });
}

/**
 * Bulk candidates for a whole page of transactions in one HTTP call. The feed
 * uses this so each row can render "Match → JE-0012 · DR Office Supplies"
 * inline without firing N parallel requests when the page loads.
 */
export function useBulkMatchCandidates(companyId: string | null, transactionIds: string[]) {
  // Stable cache key — sort ids so the same set in different orders hits the
  // same cache entry.
  const sortedIds = [...transactionIds].sort();
  return useQuery({
    queryKey: ['match-candidates-bulk', companyId ?? 'none', sortedIds.join(',')] as const,
    enabled: companyId !== null && transactionIds.length > 0,
    queryFn: () =>
      api
        .post<{ candidates: Record<string, MatchCandidate[]> }>(
          `/companies/${companyId}/bank-transactions/match-candidates/bulk`,
          { transactionIds: sortedIds },
        )
        .then((r) => r.candidates),
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });
}

export const existingEntryMatchesKey = (companyId: string | null) =>
  ['existing-entry-matches-bulk', companyId ?? 'none'] as const;

/**
 * Proactive "you already posted this" suggestions for the unmatched rows on the
 * current page: journal entries the owner keyed by hand that look like the same
 * transaction the bank just delivered. Returned keyed by transaction id so a row
 * can render its own suggestion without another round-trip.
 */
export function useExistingEntryMatches(
  companyId: string | null,
  transactionIds: string[],
) {
  // Sorted so the same page hits the same cache entry regardless of row order.
  const sortedIds = [...transactionIds].sort();
  return useQuery({
    queryKey: [...existingEntryMatchesKey(companyId), sortedIds.join(',')] as const,
    enabled: companyId !== null && transactionIds.length > 0,
    queryFn: () =>
      api
        .post<{ suggestions: BankTransactionSuggestion[] }>(
          `/companies/${companyId}/bank-transactions/existing-entry-matches/bulk`,
          { transactionIds: sortedIds },
        )
        .then((r) => {
          const byTx: Record<string, ExistingEntryMatch[]> = {};
          for (const s of r.suggestions) byTx[s.bankTransactionId] = s.matches;
          return byTx;
        }),
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });
}

export interface MatchedOffsetInfo {
  accountCode: string;
  accountName: string;
  isSplit: boolean;
}

export interface ReclassifyResult {
  bankTransactionId: string;
  journalEntryId: string;
  oldAccountId: string;
  newAccountId: string;
}

export interface ReclassifyBulkResult {
  succeeded: string[];
  failed: { bankTransactionId: string; reason: string }[];
}

/**
 * Re-point the offset of a matched/reconciled bank transaction to a different
 * expense account. Bank-side line stays intact so reconciliation is unaffected.
 */
export function useReclassifyBankTransaction(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ transactionId, accountId }: { transactionId: string; accountId: string }) => {
      if (!companyId) throw new Error('No active company');
      return api
        .post<{ result: ReclassifyResult }>(
          `/companies/${companyId}/bank-transactions/${transactionId}/reclassify`,
          { accountId },
        )
        .then((r) => r.result);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bank-transactions', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['matched-offsets-bulk', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['accounts-balances', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['journal-entries', companyId ?? 'none'] });
    },
  });
}

export function useReclassifyBankTransactionsBulk(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ transactionIds, accountId }: { transactionIds: string[]; accountId: string }) => {
      if (!companyId) throw new Error('No active company');
      return api
        .post<{ result: ReclassifyBulkResult }>(
          `/companies/${companyId}/bank-transactions/reclassify-bulk`,
          { transactionIds, accountId },
        )
        .then((r) => r.result);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bank-transactions', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['matched-offsets-bulk', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['accounts-balances', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['journal-entries', companyId ?? 'none'] });
    },
  });
}

export interface ApplyRulesResult {
  txnsExamined: number;
  rulesMatched: number;
  autoPosted: number;
  suppressedByCeiling: number;
  suggested: number;
}

/**
 * Run all active rules against this company's currently-unmatched transactions.
 * Backs the "Apply rules" button. Scoped to a single bank account if supplied,
 * otherwise covers every account in the company.
 */
export function useApplyRulesToCompany(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (bankAccountId?: string | null) => {
      if (!companyId) throw new Error('No active company');
      return api
        .post<{ result: ApplyRulesResult }>(
          `/companies/${companyId}/bank-transactions/apply-rules`,
          bankAccountId ? { bankAccountId } : {},
        )
        .then((r) => r.result);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bank-transactions', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['accounts-balances', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['journal-entries', companyId ?? 'none'] });
    },
  });
}

/**
 * For the matched transactions on a page, fetch the offset account each was
 * linked to. Feeds the "Offset" column on the Matched tab.
 */
export function useBulkMatchedOffsets(companyId: string | null, transactionIds: string[]) {
  const sortedIds = [...transactionIds].sort();
  return useQuery({
    queryKey: ['matched-offsets-bulk', companyId ?? 'none', sortedIds.join(',')] as const,
    enabled: companyId !== null && transactionIds.length > 0,
    queryFn: () =>
      api
        .post<{ offsets: Record<string, MatchedOffsetInfo> }>(
          `/companies/${companyId}/bank-transactions/matched-offsets/bulk`,
          { transactionIds: sortedIds },
        )
        .then((r) => r.offsets),
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });
}

/**
 * Pair an unmatched bank txn with another unmatched bank txn (CC payment,
 * transfer between own accounts). Posts a single transfer JE behind the scenes
 * and links both bank rows.
 */
export function useMatchTransferPair(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      transactionId,
      pairBankTransactionId,
    }: {
      transactionId: string;
      pairBankTransactionId: string;
    }) => {
      if (!companyId) throw new Error('No active company');
      return api.post<{ journalEntryId: string; transactionIds: [string, string] }>(
        `/companies/${companyId}/bank-transactions/${transactionId}/match-transfer-pair`,
        { pairBankTransactionId },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bank-transactions', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['accounts-balances', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['journal-entries', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['match-candidates', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['match-candidates-bulk', companyId ?? 'none'] });
    },
  });
}

export function useMatchToExisting(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ transactionId, journalEntryId }: { transactionId: string; journalEntryId: string }) => {
      if (!companyId) throw new Error('No active company');
      return api.post<{ transactionId: string; journalEntryId: string }>(
        `/companies/${companyId}/bank-transactions/${transactionId}/match-existing`,
        { journalEntryId },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bank-transactions', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['accounts-balances', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['match-candidates-bulk', companyId ?? 'none'] });
      // The entry we just linked is no longer a candidate for anything else.
      qc.invalidateQueries({ queryKey: existingEntryMatchesKey(companyId) });
    },
  });
}

/**
 * Settle an open receivable with a deposit: DR bank / CR A/R, no new revenue.
 * Used when an accrual (revenue recognised when earned) is finally paid.
 */
export function useSettleReceivable(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ transactionId, receivableLineId }: { transactionId: string; receivableLineId: string }) => {
      if (!companyId) throw new Error('No active company');
      return api.post<{ transactionId: string; journalEntryId: string; settled: string }>(
        `/companies/${companyId}/bank-transactions/${transactionId}/settle-receivable`,
        { receivableLineId },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bank-transactions', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['accounts-balances', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['match-candidates-bulk', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: existingEntryMatchesKey(companyId) });
      // The receivable's outstanding balance changed — every other deposit's
      // candidate list must be recomputed or a settled accrual stays on offer.
      qc.invalidateQueries({ queryKey: ['open-receivables', companyId ?? 'none'] });
    },
  });
}

/**
 * Payroll accruals awaiting payment — one row per person per cheque, for the
 * tick-box settle panel. Not filtered by date: the bank drafts direct deposits
 * BEFORE the pay date, so the accrual being settled is often dated later.
 */
export function useOpenPayables(companyId: string | null, enabled = true) {
  return useQuery({
    queryKey: ['open-payables', companyId ?? 'none'],
    enabled: companyId !== null && enabled,
    queryFn: () =>
      api
        .get<{ payables: OpenPayableItem[] }>(`/companies/${companyId}/open-payables`)
        .then((r) => r.payables),
  });
}

/**
 * Settle one payment against one or more payroll accruals. Posts a single
 * combining entry — one debit line per person, each linked to the accrual it
 * clears — then matches the bank row to it. This is how one bulk draft claims
 * several journal entries without any change to how matching is stored.
 */
export function useSettlePayables(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      transactionId,
      journalLineIds,
    }: {
      transactionId: string;
      journalLineIds: string[];
    }) => {
      if (!companyId) throw new Error('No active company');
      return api.post<{
        transactionId: string;
        journalEntryId: string;
        settled: string;
        itemCount: number;
      }>(`/companies/${companyId}/bank-transactions/${transactionId}/settle-payables`, {
        journalLineIds,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bank-transactions', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['accounts-balances', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['match-candidates-bulk', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: existingEntryMatchesKey(companyId) });
      // Those accruals are no longer open — every other payment's candidate
      // list must be recomputed or a settled cheque stays on offer.
      qc.invalidateQueries({ queryKey: ['open-payables', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['payroll-uncashed', companyId ?? 'none'] });
    },
  });
}

export function useMatchAsIntercompany(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      transactionId,
      input,
    }: {
      transactionId: string;
      input: MatchBankAsIntercompanyInput;
    }) => {
      if (!companyId) throw new Error('No active company');
      return api.post<{ transactionId: string; journalEntryId: string; intercompanyId: string }>(
        `/companies/${companyId}/bank-transactions/${transactionId}/match-intercompany`,
        input,
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bank-transactions', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['journal-entries', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['accounts-balances', companyId ?? 'none'] });
    },
  });
}

export function useUnmatchBankTransaction(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (transactionId: string) => {
      if (!companyId) throw new Error('No active company');
      return api.post<{ transactionId: string }>(
        `/companies/${companyId}/bank-transactions/${transactionId}/unmatch`,
        {},
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bank-transactions', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['journal-entries', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['accounts-balances', companyId ?? 'none'] });
      // Unlinking frees the entry to be suggested again.
      qc.invalidateQueries({ queryKey: existingEntryMatchesKey(companyId) });
    },
  });
}

export function useExcludeBankTransaction(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      transactionId,
      reason,
    }: {
      transactionId: string;
      reason?: string;
    }) => {
      if (!companyId) throw new Error('No active company');
      return api.post<{ transaction: BankTransaction }>(
        `/companies/${companyId}/bank-transactions/${transactionId}/exclude`,
        reason ? { reason } : {},
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bank-transactions', companyId ?? 'none'] });
    },
  });
}

export function useUnexcludeBankTransaction(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (transactionId: string) => {
      if (!companyId) throw new Error('No active company');
      return api.post<{ transaction: BankTransaction }>(
        `/companies/${companyId}/bank-transactions/${transactionId}/unexclude`,
        {},
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bank-transactions', companyId ?? 'none'] });
    },
  });
}

// ── Bank rules ────────────────────────────────────────────────────────────

export const bankRulesKey = (
  companyId: string | null,
  includeInactive = false,
) =>
  ['bank-rules', companyId ?? 'none', includeInactive ? 'all' : 'active'] as const;

export function useBankRules(
  companyId: string | null,
  opts: { includeInactive?: boolean } = {},
) {
  return useQuery({
    queryKey: bankRulesKey(companyId, opts.includeInactive),
    enabled: companyId !== null,
    queryFn: () => {
      const qs = opts.includeInactive ? '?includeInactive=true' : '';
      return api
        .get<{ bankRules: BankRule[] }>(
          `/companies/${companyId}/bank-rules${qs}`,
        )
        .then((r) => r.bankRules);
    },
  });
}

export function useCreateBankRule(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateBankRuleInput) => {
      if (!companyId) throw new Error('No active company');
      return api
        .post<{ bankRule: BankRule }>(
          `/companies/${companyId}/bank-rules`,
          input,
        )
        .then((r) => r.bankRule);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bank-rules', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['rule-suggestions', companyId ?? 'none'] });
    },
  });
}

export function useUpdateBankRule(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateBankRuleInput }) => {
      if (!companyId) throw new Error('No active company');
      return api
        .patch<{ bankRule: BankRule }>(
          `/companies/${companyId}/bank-rules/${id}`,
          input,
        )
        .then((r) => r.bankRule);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bank-rules', companyId ?? 'none'] });
    },
  });
}

export function useDeactivateBankRule(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      if (!companyId) throw new Error('No active company');
      return api.delete<{ bankRule: BankRule }>(
        `/companies/${companyId}/bank-rules/${id}`,
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bank-rules', companyId ?? 'none'] });
    },
  });
}

export function useReorderBankRules(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ReorderBankRulesInput) => {
      if (!companyId) throw new Error('No active company');
      return api.put<{ bankRules: BankRule[] }>(
        `/companies/${companyId}/bank-rules/reorder`,
        input,
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bank-rules', companyId ?? 'none'] });
    },
  });
}

// ── History-based suggestions ─────────────────────────────────────────────

/** Recompute per-transaction category suggestions from past matched transactions. */
export function useSuggestCategories(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (bankAccountId?: string) => {
      if (!companyId) throw new Error('No active company');
      return api.post<SuggestCategoriesResult>(
        `/companies/${companyId}/bank/suggest-categories`,
        bankAccountId ? { bankAccountId } : {},
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bank-transactions', companyId ?? 'none'] });
    },
  });
}

export const ruleSuggestionsKey = (companyId: string | null) =>
  ['rule-suggestions', companyId ?? 'none'] as const;

/** Rules the system proposes from recurring history patterns. */
export function useRuleSuggestions(companyId: string | null) {
  return useQuery({
    queryKey: ruleSuggestionsKey(companyId),
    enabled: companyId !== null,
    queryFn: () =>
      api
        .get<{ suggestions: RuleSuggestion[] }>(
          `/companies/${companyId}/bank/rule-suggestions`,
        )
        .then((r) => r.suggestions),
  });
}

/**
 * Filter-driven reclassify. Dry run by default; the same call with
 * `dryRun: false` applies up to the per-call cap and reports `remaining`.
 */
export function useReclassifyByAccount(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ReclassifyByAccountInput) => {
      if (!companyId) throw new Error('No active company');
      return api
        .post<{ result: ReclassifyByAccountResult }>(
          `/companies/${companyId}/bank-transactions/reclassify-by-account`,
          input,
        )
        .then((r) => r.result);
    },
    onSuccess: (res) => {
      if (res.dryRun) return;
      qc.invalidateQueries({ queryKey: ['bank-transactions', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['matched-offsets-bulk', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['accounts-balances', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['journal-entries', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['report'] });
    },
  });
}
